import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PublicSettingsProvider } from "@/context/PublicSettingsContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Layout from "@/components/Layout";
import { ChangePasswordScreen } from "@/components/ChangePasswordScreen";
import SeoHead from "@/components/SeoHead";
import Dashboard from "@/pages/Dashboard";
import Properties from "@/pages/Properties";
import PropertyDetail from "@/pages/PropertyDetail";
import Landlords from "@/pages/Landlords";
import LandlordDetail from "@/pages/LandlordDetail";
import Reminders from "@/pages/Reminders";
import Settings from "@/pages/Settings";
import VoiceNotes from "@/pages/VoiceNotes";
import Investors from "@/pages/Investors";
import TaskManager from "@/pages/TaskManager";
import Issues from "@/pages/Issues";
import Expenses from "@/pages/Expenses";
import Conversations from "@/pages/Conversations";
import Corporate from "@/pages/Corporate";
import Cleaners from "@/pages/Cleaners";
import PublicOwnerSignup from "@/pages/PublicOwnerSignup";
import Newsletter from "@/pages/Newsletter";
import PublicIssue from "@/pages/PublicIssue";
import PublicOnboarding from "@/pages/PublicOnboarding";
import PublicCleaning from "@/pages/PublicCleaning";
import PublicProposal from "@/pages/PublicProposal";
import ResetPassword from "@/pages/ResetPassword";

function Protected({ children, allow }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground overline">Loading Bides…</div>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ChangePasswordScreen />;
  if (allow && !allow.includes(user.role)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

const STAFF = ["admin", "operations", "compliance"];

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <PublicSettingsProvider>
        <SeoHead />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/reset" element={<ResetPassword />} />
            <Route path="/report/:token" element={<PublicIssue />} />
            <Route path="/clean/:token" element={<PublicCleaning />} />
            <Route path="/onboarding/:token" element={<PublicOnboarding />} />
            <Route path="/proposal/:token" element={<PublicProposal />} />
            <Route path="/owner-interest" element={<PublicOwnerSignup />} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/corporate" element={<Protected allow={STAFF}><Corporate /></Protected>} />
            <Route path="/newsletter" element={<Protected allow={STAFF}><Newsletter /></Protected>} />
            <Route path="/properties" element={<Protected allow={[...STAFF, "landlord"]}><Properties /></Protected>} />
            <Route path="/properties/:id" element={<Protected allow={[...STAFF, "landlord"]}><PropertyDetail /></Protected>} />
            <Route path="/landlords" element={<Protected allow={STAFF}><Landlords /></Protected>} />
            <Route path="/landlords/:id" element={<Protected allow={STAFF}><LandlordDetail /></Protected>} />
            <Route path="/reminders" element={<Protected allow={STAFF}><Reminders /></Protected>} />
            <Route path="/investors" element={<Protected allow={STAFF}><Investors /></Protected>} />
            <Route path="/tasks" element={<Protected allow={STAFF}><TaskManager /></Protected>} />
            <Route path="/cleaners" element={<Protected allow={STAFF}><Cleaners /></Protected>} />
            <Route path="/issues" element={<Protected allow={[...STAFF, "landlord"]}><Issues /></Protected>} />
            <Route path="/expenses" element={<Protected allow={[...STAFF, "landlord"]}><Expenses /></Protected>} />
            <Route path="/conversations" element={<Protected allow={[...STAFF, "landlord"]}><Conversations /></Protected>} />
            <Route path="/settings" element={<Protected allow={["admin"]}><Settings /></Protected>} />
            <Route path="/voice-notes" element={<Protected allow={STAFF}><VoiceNotes /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        </PublicSettingsProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
